import { lazy, Suspense, useState } from 'react'

import BellIcon from '@heroicons/react/outline/esm/BellIcon.js'
import DocumentTextIcon from '@heroicons/react/outline/esm/DocumentTextIcon.js'
import DotsHorizontalIcon from '@heroicons/react/outline/esm/DotsHorizontalIcon.js'
import { Link } from 'react-router-dom'

import {
  PostType,
  Space,
  SpaceMembershipStatus,
} from '@tribeplatform/gql-client/types'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Button, ButtonSize } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { ChevronDownIcon } from '@tribeplatform/react-ui-kit/icons'

import { getTranslatedAddPostText } from '../../utils/post.js'
import { SpaceActionItems } from './SpaceActionItems.js'
import { SpaceMembershipButton } from './SpaceMembershipButton.js'

const MemberNotificationSettingsModal = lazy(() =>
  import('../Notification/MemberNotificationSettingsModal.js').then(m => ({
    default: m.MemberNotificationSettingsModal,
  })),
)

export const AddPostButton = ({
  space,
  availablePostTypes,
  size = 'lg',
}: {
  space: Space
  availablePostTypes: Array<PostType>
  size?: ButtonSize
}) => {
  const intl = useI18n()
  const { isGuest } = useAuthMember()

  /**
   * @tag Vite migration
   * Dropdown produces hydration error because of prop `id` did not match.
   * As a workaround, Dropdown is initialized client-side only
   */
  const { isFirstRender } = useFirstRenderContext()
  if (isGuest || !availablePostTypes?.length) {
    return null
  }

  if (availablePostTypes.length === 1) {
    const postType = availablePostTypes[0]
    return (
      <Button
        size={size}
        as={Link}
        to={`${space.relativeUrl}/new?post_type=${postType?.id}`}
      >
        {getTranslatedAddPostText(intl, postType.name)}
      </Button>
    )
  }

  const activator = (
    <Button variant="primary" size={size} trailingIcon={<ChevronDownIcon />}>
      <T defaultMessage="Add post" id="Posts.Add" />
    </Button>
  )

  if (isFirstRender) {
    return activator
  }

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.ButtonMinimal as="div">{activator}</Dropdown.ButtonMinimal>
      <Dropdown.Items>
        {availablePostTypes.map(postType => (
          <Dropdown.Item
            key={postType.id}
            leadingIcon={<DocumentTextIcon />}
            as={Link}
            to={`${space.relativeUrl}/new?post_type=${postType.id}`}
          >
            {getTranslatedAddPostText(intl, postType.name)}
          </Dropdown.Item>
        ))}
      </Dropdown.Items>
    </Dropdown>
  )
}

export const SpaceActionButtons = ({ space }: { space: Space }) => {
  const { $t } = useI18n()
  /**
   * @tag Vite migration
   * Dropdown produces hydration error because of prop `id` did not match.
   * As a workaround, Dropdown is initialized client-side only
   */
  const { isFirstRender } = useFirstRenderContext()

  const [notificationModal, setNotificationModal] = useState<boolean>(false)

  const joined =
    space.authMemberProps?.membershipStatus === SpaceMembershipStatus.joined

  const availablePostTypes = space.authMemberProps?.availablePostTypes || []

  return (
    <>
      {joined && (
        <AddPostButton availablePostTypes={availablePostTypes} space={space} />
      )}
      {joined && (
        <Button
          variant="outline"
          onClick={() => setNotificationModal(true)}
          leadingIcon={<BellIcon />}
          size="lg"
        />
      )}
      <SpaceMembershipButton space={space} />
      {isFirstRender ? (
        <Button
          variant="outline"
          leadingIcon={<DotsHorizontalIcon />}
          size="lg"
        />
      ) : (
        <Dropdown placement="bottom-end">
          <Dropdown.ButtonMinimal
            aria-label={$t({
              defaultMessage: 'Options',
              id: 'Generics.Options',
            })}
          >
            <Button
              as="span"
              variant="outline"
              leadingIcon={<DotsHorizontalIcon />}
              size="lg"
            />
          </Dropdown.ButtonMinimal>
          <SpaceActionItems space={space} />
        </Dropdown>
      )}
      <Suspense fallback={<></>}>
        <MemberNotificationSettingsModal
          spaceId={space.id}
          open={notificationModal}
          onClose={() => setNotificationModal(false)}
        />
      </Suspense>
    </>
  )
}
